import { ReactNode, useContext, useEffect, useState } from 'react';
import {
  ChevronButton,
  Heading,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import bgStart from '../../../assets/images/bg_start.svg';
import bgEnd from '../../../assets/images/bgEnd.png';
import { DTVendable } from '@rabbit/data/types';
import { AppContext } from '@rabbit/app-context';
import { subscribeDocument } from '@rabbit/utils/api';

export interface AuthContainerProps {
  children: ReactNode;
  goBack?: null | (() => any);
  title?: string;
  background?: boolean;
  extraClasses?: string;
  currentStep?: number | null;
  loading?: boolean;
}

export function AuthContainer({
  children,
  goBack,
  title,
  background = false,
  extraClasses = '',
  currentStep,
  loading,
}: AuthContainerProps) {
  const [vendableLoading, setVendableLoading] = useState(false);
  const [vendable, setVendable] = useState<DTVendable>();
  const { tenantInfo } = useContext(AppContext);

  useEffect(() => {
    let unSub = () => {};
    if (localStorage.vendableId) {
      setVendableLoading(true);
      unSub = subscribeDocument<DTVendable>(
        `vendable/${localStorage.vendableId}`,
        (data) => {
          setVendable(data.data());
          setVendableLoading(false);
          localStorage.removeItem('vendableId');
        }
      );
    }

    return () => {
      unSub();
    };
  }, []);

  const BackAndTitle = (
    <div className="relative mb-4 flex w-full flex-row items-center sm:mb-6 md:mb-8">
      {goBack && (
        <ChevronButton
          className="absolute left-0 flex h-full cursor-pointer flex-wrap content-center"
          kind="back"
          onClick={goBack}
        />
      )}

      {title && (
        <div className="mx-auto text-center">
          <Heading kind="h2" className="text-xl sm:text-2xl md:text-3xl">{title}</Heading>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-canvas flex grow flex-col py-4 sm:py-6 md:py-8 lg:py-10 lg:content-center">
      <div
        style={{
          backgroundImage:
            !currentStep || currentStep === 1
              ? `${background ? 'url(' + bgStart + ')' : 'none'}`
              : '',
        }}
        className={`relative mx-auto my-4 w-full max-w-full px-4 sm:my-6 sm:px-6 md:my-8 ${extraClasses} ${
          currentStep === 1
            ? 'md:grid md:grid-cols-1 md:gap-8 lg:grid-cols-2 lg:gap-12 xl:w-[1008px]'
            : 'sm:max-w-[90%] md:max-w-[80%] lg:max-w-[600px]'
        }`}
      >
        {currentStep === 1 && (
          <div
            className={`relative mb-6 rounded-lg bg-white p-4 shadow-sm transition-shadow duration-300 sm:mb-8 sm:p-6 md:mb-0 md:p-8 md:shadow-md lg:p-10 lg:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)]`}
          >
            {vendableLoading && <LoadingSpinner size="sm" overlay={true} />}
            <div className="flex flex-col space-y-6 sm:space-y-8">
              <img
                src={tenantInfo?.logo}
                className="mx-auto max-h-[48px] w-full object-contain sm:max-h-[60px] md:max-h-[72px]"
              />
              <div className={`flex flex-col space-y-6 sm:space-y-8 md:space-y-10`}>
                <div className="flex flex-col space-y-4">
                  <div
                    className={`flex items-center justify-center ${
                      vendable ? 'h-[240px] sm:h-[300px] md:h-[360px]' : 'h-[260px] sm:h-[320px] md:h-[400px]'
                    } w-full overflow-hidden rounded-md`}
                  >
                    <img
                      src={
                        vendable
                          ? vendable.img && vendable.img.length > 0
                            ? vendable.img[0]
                            : tenantInfo?.productPlaceholder
                          : tenantInfo?.productPlaceholder
                      }
                      className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  {vendable && (
                    <div className="font-nunito text-center text-xl font-medium sm:text-2xl">
                      {vendable.title}
                    </div>
                  )}
                </div>
                <div className="font-nunito text-center text-base sm:text-lg md:text-xl">
                  <strong>{tenantInfo?.name}</strong> has partnered with{' '}
                  <strong className="text-primary-900">iWarranty</strong> to
                  help you manage all your products and warranties in one place.
                </div>
              </div>
            </div>
          </div>
        )}
        <div
          className={`relative rounded-lg bg-white p-4 shadow-sm transition-shadow duration-300 sm:p-6 md:p-8 md:shadow-md lg:p-10 lg:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)] ${
            currentStep !== 9 ? 'h-full' : ''
          }`}
        >
          {loading && <LoadingSpinner overlay={true} size="sm" />}
          {currentStep === 9 && <img src={bgEnd} alt={'lastScreenBg'} className="w-full" />}
          {(goBack || title) && BackAndTitle}
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthContainer;
